import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import classNames from 'classnames';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import NSButton from 'bricks/NSButton/NSButton';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import { FEATURE_IDS } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { URLS } from 'services/urls';
import { useRefreshProFormaActuals } from '../../hooks/useRefreshProFormaActuals';
import { Deal } from '../../types';
import './DealNameCell.scss';

interface DealNameCellProps {
    deal: Deal;
}

const DealNameCell: React.FC<DealNameCellProps> = ({ deal }) => {
    const { selectedOrganizationId, isFeatureAccessibleToUser } = useAuthContext();
    const { showSuccess, showError } = useToast();
    const { mutate: refreshActuals, isPending } = useRefreshProFormaActuals();

    const hasWriteAccess = isFeatureAccessibleToUser([FEATURE_IDS.PRO_FORMA], AccessLevels.READ_WRITE_ACCESS);

    const canRefreshActuals = hasWriteAccess
        && deal.activeProFormaStatusId === PRO_FORMA_STATUS.PROJECT_CREATED
        && deal.activeProFormaId != null;

    const handleRefresh = () => {
        if (!deal.activeProFormaId || !selectedOrganizationId) return;

        refreshActuals(
            { organizationId: selectedOrganizationId, dealId: deal.id, proFormaId: deal.activeProFormaId },
            {
                onSuccess: () => showSuccess('Project actuals refreshed.'),
                onError: error => showError(error.message || 'Failed to refresh project actuals.'),
            },
        );
    };

    return (
        <span className={classNames('DealNameCell d-inline-flex align-items-center', { 'DealNameCell--refreshing': isPending })}>
            <Link to={generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId: deal.id || '' })}>{deal.dealName}</Link>
            <RenderIf isTrue={canRefreshActuals}>
                <span className="DealNameCell__refresh">
                    <NSButton
                        id={`refresh-actuals-${deal.id}`}
                        icon={faSyncAlt}
                        size="sm"
                        className="py-0 bg-none hover:text-dark hover:bg-light"
                        callback={handleRefresh}
                        isLoading={isPending}
                        tooltip="Refresh project actuals"
                    />
                </span>
            </RenderIf>
        </span>
    );
};

export default DealNameCell;
